/* ============
 * Vuex Store
 * ============
 *
 * The store of the application
 *
 * http://vuex.vuejs.org/en/index.html
 */

import Vue from 'vue';
import Vuex from 'vuex';
import createLogger from 'vuex/dist/logger';
import * as actions from './actions';
import * as getters from './getters';

// Modules
import profile from './modules/profile';
import auth from './modules/auth';
import client from './modules/client';
import clientGroup from './modules/clientGroup';
import clientTypes from './modules/clientTypes';
import addressTypes from './modules/addressTypes';
import contactTypes from './modules/contactTypes';
import providerGroup from './modules/providerGroup';
import country from './modules/country';
import siteLanguages from './modules/siteLanguages';
import city from './modules/city';
import municipality from './modules/municipality';
import postal from './modules/postal';
import product from './modules/product';
import productGroup from './modules/productGroup';
import productPackaging from './modules/productPackaging';
import packagingMaterial from './modules/packagingMaterial';
import packagingType from './modules/packagingType';
import tax from './modules/tax';
import plan from './modules/plan';
import organizationType from './modules/organizationType';
import productType from './modules/productType';
import productSubtype from './modules/productSubtype';
import producer from './modules/producer';
import order from './modules/order';
import bill from './modules/bill';
import orderProduct from './modules/orderProduct';
import creditNoteProduct from './modules/creditNoteProduct';
import creditNoteReason from './modules/creditNoteReason';
import billProduct from './modules/billProduct';
import ediOrder from './modules/ediOrder';
import ediOrderItem from './modules/ediOrderItem';
import businessDay from './modules/businessDay';
import messageTemplateType from './modules/messageTemplateType';
import campaign from './modules/campaign';
import productDeposit from './modules/productDeposit';
import depositSystem from './modules/depositSystem';
import sidebar from './modules/sidebar';
import modal from './modules/modal';
import accountingProvider from './modules/accountingProvider';
import supplier from './modules/supplier';
import warehouse from './modules/warehouse';
import unitOfMeasure from './modules/unitOfMeasure';
import stock from './modules/stock';
import adjustment from './modules/adjustment';
import purchase from './modules/purchase';
import view from './modules/view';
import shippingProvider from './modules/shippingProvider';
import packageUnitType from './modules/packageUnitType';
import unitType from './modules/unitType';
import pckg from './modules/package';
import packageOrderProduct from './modules/packageOrderProduct';
import chartOfAccounts from './modules/chartOfAccounts';
import productTag from './modules/productTag';
import priceField from './modules/priceField';
import productFlow from './modules/productFlow';
import exciseGroup from './modules/exciseGroup';
import additionalCode from './modules/additionalCode';
import organizationMember from './modules/organizationMember';
import organizationMemberProductBusinesshour from './modules/organizationMemberProductBusinesshour';
import organizationMemberBooking from './modules/organizationMemberBooking';
import modules from './modules/modules';
import transactionType from './modules/transactionType';
import transfer from './modules/transfer';
import material from './modules/material';
import materialType from './modules/materialType';
import batch from './modules/batch';
import batchLog from './modules/batchLog';
import recipe from './modules/recipe';
import recipeType from './modules/recipeType';
import recipeIngredience from './modules/recipeIngredience';
import batchIngredience from './modules/batchIngredience';
import recipeProcedure from './modules/recipeProcedure';
import recipeProduct from './modules/recipeProduct';
import equipment from './modules/equipment';
import license from './modules/license';
import licenseType from './modules/licenseType';
import licensePermit from './modules/licensePermit';
import licensePermitType from './modules/licensePermitType';
import licenseGroup from './modules/licenseGroup';
import licensePermitGroup from './modules/licensePermitGroup';
import excisePeriod from './modules/excisePeriod';
import subscriberList from './modules/subscriberList';
import subscriber from './modules/subscriber';
import partner from './modules/partner';
import orderPickup from './modules/orderPickup';
import productRecommendation from './modules/productRecommendation';
import paymentMethods from './modules/paymentMethods';
import language from './modules/language';
import currency from './modules/currency';
import project from './modules/project';
import bridgeHandoff from './modules/bridgeHandoff';

Vue.use(Vuex);

const debug = process.env.NODE_ENV !== 'production';

export default new Vuex.Store({
  /**
   * Assign the actions to the store
   */
  actions,

  /**
   * Assign the getters to the store
   */
  getters,

  /**
   * Assign the modules to the store
   */
  modules: {
    // Data modules
    profile,
    auth,
    client,
    clientGroup,
    clientTypes,
    addressTypes,
    contactTypes,
    providerGroup,
    country,
    siteLanguages,
    city,
    municipality,
    postal,
    product,
    productGroup,
    productPackaging,
    packagingMaterial,
    packagingType,
    tax,
    plan,
    organizationType,
    productType,
    productSubtype,
    producer,
    order,
    bill,
    orderProduct,
    ediOrder,
    ediOrderItem,
    businessDay,
    messageTemplateType,
    creditNoteProduct,
    creditNoteReason,
    billProduct,
    campaign,
    productDeposit,
    depositSystem,
    accountingProvider,
    chartOfAccounts,
    supplier,
    warehouse,
    unitOfMeasure,
    stock,
    adjustment,
    purchase,
    view,
    shippingProvider,
    productTag,
    priceField,
    productFlow,
    exciseGroup,
    additionalCode,
    modules,
    packageUnitType,
    unitType,
    pckg,
    packageOrderProduct,
    // Layout modules
    sidebar,
    modal,
    organizationMember,
    organizationMemberProductBusinesshour,
    organizationMemberBooking,
    transactionType,
    transfer,
    material,
    materialType,
    batch,
    batchLog,
    recipe,
    recipeType,
    recipeIngredience,
    batchIngredience,
    recipeProcedure,
    recipeProduct,
    equipment,
    license,
    licenseType,
    licensePermitType,
    licensePermit,
    licensePermitGroup,
    licenseGroup,
    excisePeriod,
    subscriberList,
    subscriber,
    partner,
    orderPickup,
    productRecommendation,
    paymentMethods,
    language,
    currency,
    project,
    bridgeHandoff,
  },

  /**
   * If strict mode should be enabled
   */
  strict: debug,

  /**
   * Plugins used in the store
   */
  plugins: debug ? [createLogger()] : [],
});



// WEBPACK FOOTER //
// ./src/app/store/index.js